import {ActivityHeader} from '@github-ui/commenting/ActivityHeader'
import type {ConversationIssueComment} from '../../page-data/payloads/conversation-comments'
import {useReactToIssueCommentMutation} from '../../mutations/use-react-to-issue-comment-mutation'
import {type SelectionContext, selectQuoteFromComment} from '@github-ui/commenting/quotes'
import {use, useRef, useState} from 'react'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useLayoutCanComment} from '../../stores/layout-selectors'
import {useLayoutRouteData} from '../../page-data/loaders/use-layout-route-data'
import {CommentLayout} from './CommentLayout'
import {ConversationItemActions, ConversationItemActionsProvider} from './ConversationItemActions'
import {CommentsContext} from './CommentsContext'
import {issueCommentAnchor} from './utils/comment-anchors'
import {useFocusHashTarget} from '../../hooks/use-focus-hash-target'
import {useUpdateIssueCommentMutation} from '../../mutations/use-update-issue-comment-mutation'
import {Button, ConfirmationDialog} from '@primer/react'
import {HistoryIcon} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {useBodyEditManager} from './use-body-edit-manager'
import {ConversationItemBody} from './ConversationItemBody'
import {useDeleteIssueCommentMutation} from '../../mutations/use-delete-issue-comment-mutation'
import {useHideIssueCommentMutation} from '../../mutations/use-hide-issue-comment-mutation'
import {useUnhideIssueCommentMutation} from '../../mutations/use-unhide-issue-comment-mutation'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ConversationItemFallback} from './ConversationItemFallback'
import {useReturnFocus} from '@github-ui/use-return-focus'
import styles from './IssueCommentItem.module.css'
import {MutationFailureBanner} from '../FailureBanner'

interface IssueCommentItemProps {
  comment: ConversationIssueComment
  pullRequestAuthorLogin: string
  // Optional callback for handling quote reply actions. If provided, a "Quote reply" button will be rendered.
  onQuoteReply?: (quotedText: string) => void
  className?: string
}

function UnsafeIssueCommentItem({
  comment,
  pullRequestAuthorLogin,
  onQuoteReply: externalOnQuoteReply,
  className,
  id,
}: IssueCommentItemProps & {id: string}): React.ReactElement | null {
  const currentUserCanComment = useLayoutCanComment()
  const {helpUrl} = useLayoutRouteData()

  const commentRef = useRef<HTMLElement>(null)

  const reactToCommentMutation = useReactToIssueCommentMutation()

  const onQuoteReply =
    currentUserCanComment && externalOnQuoteReply && comment.id > 0
      ? () => {
          const current = ssrSafeWindow?.getSelection()
          let currentSelection: SelectionContext | null = null

          if (current && current.anchorNode && current.rangeCount > 0) {
            currentSelection = {
              anchorNode: current.anchorNode,
              range: current.getRangeAt(0),
            }
          }

          const quotedText = selectQuoteFromComment(commentRef.current, currentSelection, comment.body)
          externalOnQuoteReply(quotedText ?? comment.body)
        }
      : undefined

  const editBodyMutation = useUpdateIssueCommentMutation({
    onSuccess: () => announce('Comment updated successfully.'),
  })
  const directEditBodyMutation = useUpdateIssueCommentMutation()
  const editManager = useBodyEditManager(comment, editBodyMutation, directEditBodyMutation)

  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const deleteMutation = useDeleteIssueCommentMutation({
    onSuccess: () => announce('Comment deleted successfully.'),
    // we need to update the minimized state if delete fails so that the banner renders.
    // having the banner present otherwise makes it so there is weird padding that isn't worth
    // fixing with conditional CSS.
    onError: () => setIsMinimized(false),
  })

  const isHidden = comment.isHidden
  // minimized state is tracked for when a user manually expands the body of a hidden comment.
  const [isMinimized, setIsMinimized] = useState(isHidden)
  const hideMutation = useHideIssueCommentMutation()
  const unhideMutation = useUnhideIssueCommentMutation()

  const {containerRef: editorContainerRef} = useReturnFocus(commentRef)

  const actionItemsRef = useRef<HTMLButtonElement>(null)

  if (!comment.repository || deleteMutation.isPending) return null

  return (
    <>
      {confirmingDelete && (
        <ConfirmationDialog
          title="Delete comment"
          confirmButtonType="danger"
          confirmButtonContent="Delete"
          overrideButtonFocus="cancel"
          onClose={gesture => {
            setConfirmingDelete(false)

            // TODO: where to return focus to on delete? The comment does not exist.
            if (gesture === 'confirm') deleteMutation.mutate({id: comment.id})
            else setTimeout(() => actionItemsRef.current?.focus())
          }}
        >
          Are you sure you&apos;d like to delete this comment?
        </ConfirmationDialog>
      )}

      <ConversationItemActionsProvider onQuoteReply={onQuoteReply}>
        <CommentLayout
          id={id}
          ref={commentRef}
          className={className}
          tabIndex={-1}
          header={
            <ActivityHeader
              avatarUrl={comment.author?.avatarUrl || ''}
              comment={{
                ...comment,
                id: String(comment.id),
                repository: comment.repository,
              }}
              commentAuthorLogin={comment.author?.login ?? ''}
              commentSubjectType="pull request"
              commentSubjectAuthorLogin={pullRequestAuthorLogin}
              forceInlineAvatar
              isMinimized={isMinimized}
              noContainerStyles
              actions={
                editManager.isEditing || comment.id < 0 ? (
                  <>{/* hide comment actions while editing or still being submitted */}</>
                ) : (
                  <ConversationItemActions
                    authorLogin={comment.author?.login ?? ''}
                    contentId={comment.relayId}
                    contentUrl={comment.url}
                    link={comment.url}
                    navigationSource="pull_request_comment"
                    onQuoteReply={onQuoteReply}
                    onEdit={editManager.startEditIfAllowed}
                    onDelete={comment.viewerCanDelete ? () => setConfirmingDelete(true) : undefined}
                    isHidden={isHidden}
                    isMinimized={isMinimized}
                    onHide={
                      comment.viewerCanSeeMinimizeButton
                        ? reason => {
                            setIsMinimized(true)
                            hideMutation.mutate({id: comment.id, reason}, {onError: () => setIsMinimized(false)})
                          }
                        : undefined
                    }
                    githubHelpUrl={helpUrl}
                    onUnhide={
                      comment.viewerCanSeeUnminimizeButton
                        ? () => {
                            setIsMinimized(false)
                            unhideMutation.mutate({id: comment.id}, {onError: () => setIsMinimized(true)})
                          }
                        : undefined
                    }
                    onToggleMinimize={() => setIsMinimized(prev => !prev)}
                    ref={actionItemsRef}
                    organizationLogin={comment.repository.owner.login}
                    viewerCanBlockFromOrg={comment.viewerCanBlockFromOrg}
                    viewerCanBlockPersonally={comment.viewerCanBlockPersonally}
                    viewerCanUnblockFromOrg={comment.viewerCanUnblockFromOrg}
                    viewerCanUnblockPersonally={comment.viewerCanUnblockPersonally}
                  />
                )
              }
            />
          }
        >
          {!isMinimized && (
            <>
              <MutationFailureBanner
                mutation={deleteMutation}
                title="Failed to delete comment"
                returnFocusRef={actionItemsRef}
                className="mb-2"
              />
              <ConversationItemBody
                item={comment}
                reactMutation={reactToCommentMutation}
                editManager={editManager}
                editorContainerRef={editorContainerRef}
              />
              {isHidden && comment.viewerCanSeeUnminimizeButton && (
                <div className={styles.hiddenNotice}>
                  <Button
                    block
                    variant="invisible"
                    size="small"
                    onClick={() => {
                      //optimistically unhide
                      setIsMinimized(false)
                      setTimeout(() => commentRef.current?.focus(), 0)
                      unhideMutation.mutate(
                        {id: comment.id},
                        {
                          onError: () => {
                            setIsMinimized(true)
                          },
                        },
                      )
                    }}
                  >
                    <span className={styles.unhideLabel}>
                      <HistoryIcon className={styles.unhideIcon} />
                      Unhide
                    </span>
                  </Button>
                </div>
              )}
            </>
          )}
        </CommentLayout>
      </ConversationItemActionsProvider>
    </>
  )
}

export function IssueCommentItem({comment, className, ...props}: IssueCommentItemProps) {
  const anchorPrefix = use(CommentsContext).commentAnchorPrefix
  const id = issueCommentAnchor(String(comment.id), anchorPrefix)
  useFocusHashTarget(id)

  return (
    <ErrorBoundary
      fallback={<ConversationItemFallback tabIndex={-1} id={id} item={comment} className={className} />}
      boundaryName="IssueCommentItem"
    >
      <UnsafeIssueCommentItem id={id} comment={comment} className={className} {...props} />
    </ErrorBoundary>
  )
}
